import React from 'react';

import IconClose from '@components/Images/Rebranding/IconClose';
import LogoTIWithAppIconTI from '@components/Images/Rebranding/LogoTIWithAppIconTI';
import LazyImgixImage from '@components/LazyImgixImage';
import { useBlueconicContext } from '@contexts/BlueconicContext';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { captureBlueconicClickEvent } from '@utils/blueconic/utils';

const IPHONE_IMAGE_URL = 'https://tii.imgix.net/global/marketing/IPhoneTwoThirdsLarge.png';
const DOWNLOAD_APPLE_IMG_URL =
  'https://tii.imgix.net/global/marketing/download_app/DownloadAppStore.png';
const DOWNLOAD_GOOGLE_IMG_URL =
  'https://tii.imgix.net/global/marketing/download_app/DownloadGooglePlay.png';

const APPLE_APP_STORE_URL =
  'https://apps.apple.com/us/app/the-information-tech-news/id1485459932?utm_source=bc_modal&utm_campaign=app_download_modal_new_visitors';
const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.theinformation.ticker&hl=en_US&pli=1&utm_source=bc_modal&utm_campaign=app_download_modal_new_visitors';

/**
 * App download modal for new visitors.
 * Growth calls this the Acquisition P3 Modal - App Download.
 */
const AppDownloadModal = ({ onClose }) => {
  const { trackEvent } = useEventTracking();
  const { variantId } = useBlueconicContext();

  const handleAppleClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.AppDownloadApple }));
    captureBlueconicClickEvent(variantId);
    window.open(APPLE_APP_STORE_URL, '_blank');
  };

  const handleGoogleClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.AppDownloadGoogle }));
    captureBlueconicClickEvent(variantId);
    window.open(GOOGLE_PLAY_URL, '_blank');
  };

  return (
    <>
      <div className="absolute right-8 top-8 flex justify-end sm:right-16 sm:top-16">
        <IconClose
          className="h-[26px] w-[26px] cursor-pointer text-grayscale-charcoal md:text-white"
          onClick={onClose}
          data-test="close-modal"
          strokeWidth="1"
        />
      </div>
      <div className="flex h-full max-h-[420px] w-full flex-col font-suisse-screen md:flex-row">
        <div className="flex flex-col bg-white">
          <div className="p-24 sm:p-56">
            <div className="h-40 pb-12">
              <LogoTIWithAppIconTI className="h-40" />
            </div>
            <h1 className="font-headline-xl font-suisse-works sm:text-nowrap">
              Be the <span className="font-bold italic text-primary-magenta">first</span> to know
            </h1>
            <p className="font-body-lg m-0 font-suisse-works">
              From breaking the news to getting inside scoops, our reporters got the inside track on
              important stories the tech and finance leaders follow daily. Download our app today.
            </p>
            <div className="flex flex-row gap-16 pt-32">
              <div onClick={handleAppleClick}>
                <LazyImgixImage
                  alt="Pro image"
                  className="w-[140px] hover:cursor-pointer"
                  src={DOWNLOAD_APPLE_IMG_URL}
                />
              </div>
              <div onClick={handleGoogleClick}>
                <LazyImgixImage
                  alt="Pro image"
                  className="w-[140px] hover:cursor-pointer"
                  src={DOWNLOAD_GOOGLE_IMG_URL}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-end justify-center bg-grayscale-charcoal px-96">
          <LazyImgixImage
            alt="Pro image"
            className="max-h-[420px] w-full min-w-[291px] translate-y-8"
            src={IPHONE_IMAGE_URL}
            wrapperClassName="shadow-xl"
          />
        </div>
      </div>
    </>
  );
};

export default AppDownloadModal;
